<template>
  <template v-if="isLoginRoute || isFullscreenRoute">
    <router-view />
  </template>
  <MobileView v-else-if="isMobile" />
  <template v-else>
    <DashHeader
      :loading="headerLoading"
      :last-update="headerLastUpdate"
      :countdown="headerCountdown"
      :user="authUser"
      :my-count="headerMyCount"
      :my-open-count="headerMyOpenCount"
      :my-team-filter="headerMyTeamFilter"
      :my-fluxos-filter="headerMyFluxosFilter"
      @export="headerEmitExport"
      @logout="appLogout"
      @test-notif="headerEmitTestNotif"
      @toggle-my-team-filter="headerEmitToggle"
      @toggle-my-fluxos-filter="headerEmitToggleFluxos"
    />
    <div class="app-layout" :class="{ 'sidebar-open': !sidebarCollapsed }">
      <AppSidebar v-model:collapsed="sidebarCollapsed" />
      <div class="app-content">
        <!-- Telas com meta.remount (o editor do fluxo de bot e o do fluxo do ciclo são o mesmo componente) montam de novo
             ao trocar de rota ou de ciclo: nada do bot vai pro ciclo nem de um ciclo pro outro. -->
        <router-view v-slot="{ Component, route }">
          <component :is="Component" :key="route.meta.remount ? `${String(route.name)}:${String(route.params.id ?? '')}` : undefined" />
        </router-view>
      </div>
      <ConvDrawer />
      <AgentChatDrawerCp />
    </div>
    <GlobalSearch />
    <ShortcutsHelp />
    <PhoneWidget :sidebar-open="!sidebarCollapsed" />
    <InternalChatBubble :sidebar-open="!sidebarCollapsed" />
  </template>
  <!-- Sem excluir isLoginRoute de propósito: beforeinstallprompt costuma disparar
       uma única vez, bem no começo do carregamento da página — como login é a
       primeira tela de quem não está autenticado, excluir essa rota fazia o
       listener nunca estar montado a tempo de capturar o evento, perdendo a
       chance de mostrar o banner depois (nem na própria tela de login nem mais
       tarde no MobileView). SLA Telão (isFullscreenRoute) continua de fora —
       tela de TV/kiosk, sem sentido oferecer instalação ali. -->
  <PwaInstallBanner v-if="isMobile && !isFullscreenRoute && !inNativeApp" />

  <AutoLogoutWarningModal />
  <AppLockOverlay v-if="inNativeApp" />
  <ForceLogoutWarningModal />
  <AssignmentAlertModal />
  <SnoozeEndedModal v-if="!isLoginRoute" />
  <WaitingAlertBanner />
  <BroadcastComposeModal />
  <AppUpdateBanner />
  <ScreenMirrorModal v-if="!isLoginRoute && !isFullscreenRoute && !isMobile" />
  <SessionAdminModal v-if="!isLoginRoute && !isFullscreenRoute && !isMobile" />
  <ScreenMirrorIndicator v-if="!isLoginRoute" />
  <ScreenMirrorMarks v-if="!isLoginRoute" />
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, onUnmounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useStore } from 'vuex'
import Swal from 'sweetalert2'
import AppSidebar from '@/components/AppSidebar.vue'
import MobileView from '@/views/MobileView.vue'
import PwaInstallBanner from '@/components/PwaInstallBanner.vue'
import ConvDrawer from '@/components/dashboard/ConvDrawer.vue'
import AgentChatDrawerCp from '@/components/dashboard/AgentChatDrawerCp.vue'
import DashHeader from '@/components/dashboard/DashHeader.vue'
import GlobalSearch from '@/components/GlobalSearch.vue'
import PhoneWidget from '@/components/PhoneWidget.vue'
import InternalChatBubble from '@/components/InternalChatBubble.vue'
import ShortcutsHelp from '@/components/ShortcutsHelp.vue'
import AutoLogoutWarningModal from '@/components/AutoLogoutWarningModal.vue'
import ForceLogoutWarningModal from '@/components/ForceLogoutWarningModal.vue'
import AssignmentAlertModal from '@/components/AssignmentAlertModal.vue'
import WaitingAlertBanner from '@/components/WaitingAlertBanner.vue'
import { startWaitingAlerts, stopWaitingAlerts } from '@/composables/useWaitingAlert'
import { syncWebPush } from '@/composables/useWebPush'
import BroadcastComposeModal from '@/components/BroadcastComposeModal.vue'
import AppUpdateBanner from '@/components/AppUpdateBanner.vue'
import ScreenMirrorModal from '@/components/ScreenMirrorModal.vue'
import ScreenMirrorIndicator from '@/components/ScreenMirrorIndicator.vue'
import ScreenMirrorMarks from '@/components/ScreenMirrorMarks.vue'
import { screenMirrorOpen, startScreenMirrorAgent, stopScreenMirrorAgent } from '@/composables/useScreenMirror'
import SessionAdminModal from '@/components/SessionAdminModal.vue'
import { sessionAdminOpen } from '@/composables/useSessionAdmin'
import { useMobile } from '@/composables/useMobile'
import { useLogo } from '@/composables/useLogo'
import { useAutoLogout, performLogout, continueSession } from '@/composables/useAutoLogout'
import { globalSearchOpen, openGlobalSearch, closeGlobalSearch, toggleGlobalSearch } from '@/composables/useGlobalSearch'
import { shortcutsHelpOpen, toggleShortcutsHelp, closeShortcutsHelp } from '@/composables/useShortcutsHelp'
import { currentOpenConvId, closeDrawer, requestFocusReply, requestOpenMyConvs, goNextConv, goPrevConv, requestOpenChat, requestViewMonthFilter } from '@/composables/useChatTrigger'
import { loadMyTeamIds } from '@/composables/useMyTeamIds'
import { startConversationRealtime, stopConversationRealtime, reconnectRealtime, realtimeStatus } from '@/composables/useConversationRealtime'
import { isNativeApp, onAppResume } from '@/utils/nativeApp'
import { startMobilePush } from '@/composables/useMobilePush'
import { setAppLockMinutes, startAppLock } from '@/composables/useBiometricLogin'
import { getPublicSettings } from '@/services/wootdashSystemSettings'
import AppLockOverlay from '@/components/AppLockOverlay.vue'
import { checkNativeAppUpdate } from '@/composables/useNativeAppUpdate'
import { startMyActiveConversations, stopMyActiveConversations } from '@/composables/useMyActiveConversations'
import { startRailConversations, stopRailConversations } from '@/composables/useRailConversations'
import { loadCurrentAgentRole, resetCurrentAgentRole } from '@/composables/useCurrentAgentRole'
import { startSessionHeartbeat, stopSessionHeartbeat } from '@/composables/useSessionTracking'
import { useAgentChatCp } from '@/composables/useAgentChatCp'
import { useChatRailMode } from '@/composables/useChatRailMode'
import { toggleInternalChatPopupFromShortcut } from '@/composables/useInternalChatPopup'
import { usePermission } from '@/composables/usePermission'
import { useAdmin } from '@/composables/useAdmin'
import { openBroadcastCompose } from '@/composables/useBroadcastCompose'
import { useBroadcast } from '@/composables/useBroadcast'
import { pushAssignmentAlert, startAssignmentAlerts, stopAssignmentAlerts } from '@/composables/useAssignmentAlert'
import SnoozeEndedModal from '@/components/SnoozeEndedModal.vue'
import { startSnoozeAlerts, stopSnoozeAlerts } from '@/composables/useSnoozeAlerts'
import { parseYoutubeId } from '@/utils/youtube'
import type { PendingBroadcast } from '@/types/broadcast'
import {
  headerLoading, headerLastUpdate, headerCountdown,
  headerMyCount, headerMyOpenCount, headerMyTeamFilter, headerMyFluxosFilter,
  headerEmitExport, headerEmitTestNotif, headerEmitToggle, headerEmitToggleFluxos,
} from '@/composables/useHeaderState'

const route  = useRoute()
const router = useRouter()
const store  = useStore()
const sidebarCollapsed  = ref(true)
const isLoginRoute      = computed(() => route.name === 'login')
const isFullscreenRoute = computed(() => route.name === 'sla-metro')
const authUser          = computed(() => store.getters.authUser)
const { isMobile } = useMobile()

const agentChatCp = useAgentChatCp()
const { mode: chatRailMode } = useChatRailMode()
const { can } = usePermission()
const { globalSettings } = useAdmin()

// Arma o timer de logout automático por inatividade (login e SLA Telão ficam
// isentos — ver useAutoLogout.ts). Chamado uma única vez aqui: App.vue é a
// raiz da SPA e cobre tanto o layout desktop quanto o MobileView.
useAutoLogout()

// Dados do Dashboard vêm do WootDash (useWootDashLiveData + SignalR). No login só falta
// o escopo de times (resolveRoleScope / permissão por time).
watch(authUser, (user) => {
  if (user) loadMyTeamIds()
}, { immediate: true })

// App do celular (ionic/): notificações pelo Firebase pro agente logado; tocar numa notificação abre a conversa.
watch(() => authUser.value?.wootdash_token as string | undefined, token => {
  if (token && isNativeApp()) {
    void startMobilePush(token, id => requestOpenChat(id))
    // Trava ao voltar do segundo plano: o tempo vem de Configurações Gerais › App de celular.
    void getPublicSettings(token).then(s => setAppLockMinutes(s?.appLockMinutes ?? 0))
    // Versão nova do APK publicada: avisa (uma vez por versão), depois das perguntas do login.
    window.setTimeout(() => void checkNativeAppUpdate(), 6000)
  }
}, { immediate: true })

// Motor WootDash (Dashboard real + ConvDrawer portado, Fase 3 correção): o
// ConvDrawer é global (montado logo abaixo) e precisa do socket SignalR ativo e de
// "minhas conversas" atualizado mesmo em páginas que não são o Dashboard — por isso
// arma aqui, igual ao motor antigo acima, não dentro de uma view que desmonta.
watch(authUser, (user) => {
  const token = user?.wootdash_token
  if (token) {
    startConversationRealtime(token)
    startScreenMirrorAgent()
    startMyActiveConversations(token, user?.wootdash_agent_id ?? '')
    if (user?.wootdash_agent_id) loadCurrentAgentRole(token, user.wootdash_agent_id)
    startRailConversations(token, user?.wootdash_agent_id ?? '')
    startAssignmentAlerts(token, user?.wootdash_agent_id ?? '')
    // Popup "Adiamento chegou ao limite" (AdiamentoVencido do hub).
    startSnoozeAlerts(user?.wootdash_agent_id ?? '')
    // Só chega pra quem está no grupo "painel" do hub (administradores) — o backend filtra.
    startWaitingAlerts()
    // Este navegador já estava com as notificações do Windows ligadas: reconecta pro agente que entrou.
    void syncWebPush(token)
  } else {
    stopAssignmentAlerts()
    stopSnoozeAlerts()
    stopWaitingAlerts()
    stopScreenMirrorAgent()
    stopConversationRealtime()
    stopMyActiveConversations()
    resetCurrentAgentRole()
    stopRailConversations()
  }
}, { immediate: true })

// Arma o rastreio de sessão (Admin → Sessões, migração v30) — registro +
// heartbeat periódico desta aba, mesmo padrão immediate/watch(authUser) dos
// pollers acima. Ver useSessionTracking.ts.
watch(authUser, (user) => {
  if (user) startSessionHeartbeat(user, store, router)
  else stopSessionHeartbeat()
}, { immediate: true })

// Avisos em massa (tecla K): pendentes no login + novos pelo SignalR "NovoBroadcast" — ver useBroadcast.ts.
const broadcast = useBroadcast()
watch(() => authUser.value?.wootdash_token, (token) => {
  if (token) broadcast.start(token)
  else broadcast.stop()
}, { immediate: true })

function escapeHtml(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;')
}

// body/link vêm de texto livre digitado por um agente (compositor) — escapa
// antes de injetar no `html` do Swal.fire pra não abrir XSS armazenado entre
// agentes. youtubeId não precisa de escape: já sai filtrado pelo regex de
// parseYoutubeId ([\w-]{11}), não carrega HTML/aspas.
function buildBroadcastHtml(b: PendingBroadcast): string {
  const parts = [
    `<div style="text-align:left;white-space:pre-wrap;font-size:14px;line-height:1.5;margin-bottom:12px;">${escapeHtml(b.body)}</div>`,
  ]
  if (b.imageUrl) {
    parts.push(`<img src="${escapeHtml(b.imageUrl)}" style="max-width:100%;max-height:220px;border-radius:8px;object-fit:contain;margin-bottom:10px;" />`)
  }
  const ytId = b.youtubeUrl ? parseYoutubeId(b.youtubeUrl) : null
  if (ytId) {
    parts.push(`<iframe src="https://www.youtube.com/embed/${ytId}" style="width:100%;aspect-ratio:16/9;border:0;border-radius:8px;margin-bottom:10px;" allowfullscreen></iframe>`)
  }
  if (b.linkUrl) {
    parts.push(`<a href="${escapeHtml(b.linkUrl)}" target="_blank" rel="noopener noreferrer" style="color:#BC0C24;font-weight:600;word-break:break-all;">${escapeHtml(b.linkUrl)}</a>`)
  }
  return parts.join('')
}

// Guarda por id: o mesmo aviso pode voltar a entrar na fila (recarga dos pendentes) enquanto o modal está aberto.
let showingBroadcastId: string | null = null
async function showBroadcast(b: PendingBroadcast | null) {
  if (!b || showingBroadcastId === b.id) return
  // Outro alerta do Swal na tela: espera ele sair (o Swal só mostra uma janela por vez).
  if (Swal.isVisible()) { setTimeout(() => showBroadcast(broadcast.currentBroadcast.value), 700); return }
  showingBroadcastId = b.id
  const result = await Swal.fire({
    title: b.kind === 'relatorio' ? 'Relatório de Resolução' : b.kind === 'ciclo' ? 'Ciclo de envio' : 'Aviso',
    html: buildBroadcastHtml(b),
    confirmButtonText: 'Confirmado',
    confirmButtonColor: '#BC0C24',
    allowOutsideClick: false,
    allowEscapeKey: false,
  })
  showingBroadcastId = null
  // Só confirma a leitura com o clique em "Confirmado". Se outro Swal tomou o lugar (toast, erro…), o aviso
  // fechou sem o agente ler: volta a aparecer assim que a outra janela sair.
  if (!result.isConfirmed) { setTimeout(() => showBroadcast(broadcast.currentBroadcast.value), 700); return }
  await broadcast.ack(b.id)
}
watch(() => broadcast.currentBroadcast.value, b => { showBroadcast(b) })

function onKeydown(e: KeyboardEvent) {
  // Ctrl+K / Cmd+K — sempre
  if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
    e.preventDefault()
    toggleGlobalSearch()
    return
  }

  // Ctrl+! — só preview visual do popup "Novo atendimento atribuído"
  // (AssignmentAlertModal), não atribui nada de verdade. ConvId sintético
  // (negativo, timestamp) pra nunca colidir com uma conversa real e sempre
  // empilhar uma nova prévia mesmo repetindo o atalho.
  if ((e.ctrlKey || e.metaKey) && e.key === '!') {
    e.preventDefault()
    pushAssignmentAlert({ convId: -Date.now(), contactName: 'Cliente de Teste' })
    return
  }

  // Ctrl+@ — Espelho ao vivo: escolhe um agente e vê a tela do sistema dele (permissão agents.screen_view).
  // "@" é Shift+2 tanto no teclado ABNT2 quanto no americano, então e.key já chega como '@'.
  if ((e.ctrlKey || e.metaKey) && (e.key === '@' || (e.shiftKey && e.code === 'Digit2'))) {
    if (!can('agents.screen_view')) return
    e.preventDefault()
    screenMirrorOpen.value = !screenMirrorOpen.value
    return
  }

  // Ctrl+Shift+S — Sessões dos agentes: revoga sessões e desconecta agentes (permissão sessions.manage, perfil Sistema).
  // e.code em vez de e.key: com Shift o key vem 'S' e, com Caps Lock, 's'.
  if ((e.ctrlKey || e.metaKey) && e.shiftKey && !e.altKey && e.code === 'KeyS') {
    if (!can('sessions.manage')) return
    e.preventDefault()
    sessionAdminOpen.value = !sessionAdminOpen.value
    return
  }

  // Ctrl+Shift+C — popup de conversas (chat interno + diretório). Sem o Google Workspace configurado, abre um exemplo.
  // e.code pelo mesmo motivo do Ctrl+Shift+S. Vale até dentro de campo de texto (não colide com digitação).
  if ((e.ctrlKey || e.metaKey) && e.shiftKey && !e.altKey && e.code === 'KeyC') {
    e.preventDefault()
    toggleInternalChatPopupFromShortcut()
    return
  }

  // Esc — fecha o que estiver aberto
  if (e.key === 'Escape') {
    if (shortcutsHelpOpen.value)            { closeShortcutsHelp(); return }
    if (globalSearchOpen.value)             { closeGlobalSearch();  return }
    if (currentOpenConvId.value !== null)   { closeDrawer();        return }
    return
  }

  // Atalhos de uma tecla nunca disparam com modificador — senão Ctrl+C pra copiar
  // texto selecionado (e.key continua 'c' com Ctrl segurado) abriria "Minhas
  // Conversas" em vez de copiar.
  if (e.ctrlKey || e.metaKey || e.altKey) return

  // Atalhos de uma tecla: não disparar dentro de inputs/textareas
  const tag = (document.activeElement?.tagName ?? '').toUpperCase()
  const editing = tag === 'INPUT' || tag === 'TEXTAREA' ||
    (document.activeElement as HTMLElement | null)?.isContentEditable
  if (editing) return

  switch (e.key) {
    case '/':
      e.preventDefault()
      openGlobalSearch()
      break
    case '?':
      e.preventDefault()
      toggleShortcutsHelp()
      break
    case 'n': case 'N':
      e.preventDefault()
      goNextConv()
      break
    case 'p': case 'P':
      e.preventDefault()
      goPrevConv()
      break
    case 'r': case 'R':
      e.preventDefault()
      requestFocusReply()
      break
    case 'c': case 'C':
      e.preventDefault()
      requestOpenMyConvs()
      break
    case 'k': case 'K':
      if (!can('broadcast.send') || !globalSettings.value.broadcastEnabled) return
      e.preventDefault()
      openBroadcastCompose()
      break
    case 'm': case 'M':
      if (!can('foco.view') || route.name === 'foco') return
      e.preventDefault()
      router.push({ name: 'foco' })
      break
  }
}

const { loadLogo } = useLogo()

// Abre a conversa indicada por uma notificação clicada — seja via aba nova
// (query param ?conv=) ou aba já existente (mensagem do service worker),
// nunca navegando para o Chatwoot.
function onSWMessage(e: MessageEvent) {
  if (e.data?.type === 'notif-open-conv' && e.data.convId) {
    requestOpenChat(String(e.data.convId))
  } else if (e.data?.type === 'notif-open-agent-chat-cp' && e.data.senderId) {
    chatRailMode.value = 'agent'
    agentChatCp.panelOpen.value = true
    agentChatCp.openThread(authUser.value?.wootdash_token ?? '', String(e.data.senderId))
  } else if (e.data?.type === 'notif-continue-session') {
    continueSession(store)
  } else if (e.data?.type === 'notif-view-month-filter') {
    requestViewMonthFilter()
    if (route.name !== 'dashboard') router.push({ name: 'dashboard' })
  }
}

// Dentro do app do celular (ionic/): o aviso "instalar o app" é do PWA e some; ao voltar do segundo plano, reconecta o
// tempo real se o sistema derrubou a conexão nesse meio-tempo.
const inNativeApp = isNativeApp()
let stopResume: (() => void) | null = null
let stopAppLock: (() => void) | null = null

onMounted(() => {
  window.addEventListener('keydown', onKeydown)
  loadLogo()
  stopResume = onAppResume(() => { if (authUser.value && realtimeStatus.value !== 'connected') reconnectRealtime() })
  stopAppLock = startAppLock(() => !!authUser.value)

  const convParam = route.query.conv
  if (convParam) {
    requestOpenChat(String(convParam))
    const { conv, ...rest } = route.query
    router.replace({ query: rest })
  }

  // "Abrir no WootDash" das mensagens do app no Google Chat: ?chat-interno=<id do colega> abre o chat interno com ele.
  const peerParam = route.query['chat-interno']
  if (peerParam) {
    chatRailMode.value = 'agent'
    agentChatCp.panelOpen.value = true
    void agentChatCp.openThread(authUser.value?.wootdash_token ?? '', String(peerParam))
    const query = { ...route.query }
    delete query['chat-interno']
    router.replace({ query })
  }

  navigator.serviceWorker?.addEventListener('message', onSWMessage)
})
onUnmounted(() => {
  stopResume?.()
  stopAppLock?.()
  window.removeEventListener('keydown', onKeydown)
  navigator.serviceWorker?.removeEventListener('message', onSWMessage)
})

async function appLogout() {
  await performLogout(store, router)
}
</script>

<style>
.app-layout {
  display: flex;
  min-height: 100vh;
}

.app-content {
  flex: 1;
  min-width: 0;
  margin-left: 64px;
  margin-right: 60px;
  transition: margin-left 0.26s cubic-bezier(0.4, 0, 0.2, 1);
}

.app-layout.sidebar-open .app-content {
  margin-left: 250px;
}
</style>
